import type { CSSProperties } from 'react';

import classNames from 'classnames';

import type { PodcastEpisodeData } from '@/types.js';
import { TooltipWrapper } from '@/ui/TooltipWrapper';

import { useAnchorable } from '../../hooks/useAnchorable';

import { AudioPlayer } from './components/AudioPlayer';

import * as styles from './PodcastEpisodeEmbed.css';
import type { TPostPodcastEpisodeEmbedProps } from './PodcastEpisodeEmbed.types';
import { usePodcastEpisodeEmbedInteraction } from './PodcastEpisodeEmbedInteractionContext';
import {
  PODCAST_EMBED_PROGRESS_DARK_FILL,
  PODCAST_EMBED_PROGRESS_DARK_TRACK,
  PODCAST_EMBED_PROGRESS_LIGHT_FILL,
  PODCAST_EMBED_PROGRESS_LIGHT_TRACK,
} from './podcastEmbedProgressTrackColors';

const PODCAST_EMBED_DEFAULT_GRADIENT_COLOR_TOP = '#5F5F5F';
const PODCAST_EMBED_DEFAULT_GRADIENT_COLOR_BOTTOM_FALLBACK = '#2C2C2C';

const PODCAST_EMBED_TEXT_TONE_COLORS = {
  light: {
    text: '#F9FAFB',
    scrubBar: PODCAST_EMBED_PROGRESS_LIGHT_TRACK,
    scrubProgress: PODCAST_EMBED_PROGRESS_LIGHT_FILL,
  },
  dark: {
    text: '#111111',
    scrubBar: PODCAST_EMBED_PROGRESS_DARK_TRACK,
    scrubProgress: PODCAST_EMBED_PROGRESS_DARK_FILL,
  },
} as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function unwrapPodcastEpisodeEmbedData(
  raw: TPostPodcastEpisodeEmbedProps['element']['attrs']['data'],
): PodcastEpisodeData | null {
  if (raw == null) {
    return null;
  }
  if (typeof raw === 'string') {
    const trimmed = (raw as string).trim();
    if (!trimmed) {
      return null;
    }
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (!isRecord(parsed)) {
        return null;
      }
      return parsed as PodcastEpisodeData;
    } catch {
      return null;
    }
  }
  if (!isRecord(raw)) {
    return null;
  }
  return raw;
}

export const PostPodcastEpisodeEmbed = (props: TPostPodcastEpisodeEmbedProps) => {
  const { element, isLive, isEmail } = props;
  const { attrs } = element;
  const { anchorAttributes } = useAnchorable(attrs);
  const podcastEpisodeEmbedInteraction = usePodcastEpisodeEmbedInteraction();

  const embedData = unwrapPodcastEpisodeEmbedData(attrs.data);
  if (!embedData) {
    return null;
  }

  const customArtworkUrl =
    typeof attrs.imageUrl === 'string' && attrs.imageUrl.trim() !== '' ? attrs.imageUrl.trim() : null;

  const { audio_duration_display, audio_url, audio_type, duration, thumbnail_url, title, show } = embedData;

  const showImage = attrs.displayImage !== false && attrs.displayImage !== 'false';
  const showShowTitleLine = Boolean(show?.title?.trim());

  const displayArtworkUrl = customArtworkUrl ?? thumbnail_url;

  const isPlaybackLocked = podcastEpisodeEmbedInteraction?.getIsPlaybackLocked?.(audio_url);
  const tooltipText = podcastEpisodeEmbedInteraction?.getTooltipText?.(audio_url);

  const imageVerticalAlign =
    attrs.imageVerticalAlign === 'middle' || attrs.imageVerticalAlign === 'bottom' ? attrs.imageVerticalAlign : 'top';
  const alignItems =
    imageVerticalAlign === 'bottom' ? 'flex-end' : imageVerticalAlign === 'middle' ? 'center' : 'flex-start';
  const resolveGradientColorAttr = (primary: unknown, legacy: unknown): string | null => {
    if (typeof primary === 'string' && primary.trim() !== '') {
      return primary.trim();
    }
    if (typeof legacy === 'string' && legacy.trim() !== '') {
      return legacy.trim();
    }
    return null;
  };

  const gradientColorTop =
    resolveGradientColorAttr(
      attrs.gradientColorTop,
      attrs.topGradientColor ?? attrs.topGradientFallbackColor ?? attrs.backgroundColor,
    ) ?? PODCAST_EMBED_DEFAULT_GRADIENT_COLOR_TOP;

  const gradientColorBottomFallback =
    resolveGradientColorAttr(
      attrs.gradientColorBottomFallback,
      attrs.bottomGradientFallbackColor ?? attrs.bottomGradientColor ?? attrs.foregroundColor,
    ) ?? PODCAST_EMBED_DEFAULT_GRADIENT_COLOR_BOTTOM_FALLBACK;

  const textColorTone = attrs.textColorTone === 'dark' ? 'dark' : 'light';
  const useGradient = attrs.useGradient !== false && attrs.useGradient !== 'false';
  const toneColors = PODCAST_EMBED_TEXT_TONE_COLORS[textColorTone];

  const chromeOnWidgetColor = toneColors.text;
  const titleTextColor = toneColors.text;
  const gradientBackground = `linear-gradient(180deg, ${gradientColorTop} 0%, ${gradientColorBottomFallback} 100%)`;
  const resolvedBackground = useGradient ? gradientBackground : gradientColorBottomFallback;
  const audioPlayerTrackColor = toneColors.scrubBar;
  const audioPlayerTrackProgressColor = toneColors.scrubProgress;

  const audioPlayerSharedProps = {
    audioUrl: audio_url,
    audioType: audio_type,
    duration,
    durationDisplay: audio_duration_display,
    iconColor: chromeOnWidgetColor,
    primaryColor: chromeOnWidgetColor,
    trackColor: audioPlayerTrackColor,
    trackProgressColor: audioPlayerTrackProgressColor,
    textColor: chromeOnWidgetColor,
    playButtonSize: 24,
    skipButtonSize: 24,
    audioElementId: attrs.id,
    onPlayAttempt: !audio_url ? podcastEpisodeEmbedInteraction?.onPlayAttempt : undefined,
    isPlaybackLocked,
  } as const;

  const showTitleFontSize = isLive ? '13px' : '11px';
  const showTitleLineHeight = isLive ? '15px' : '11px';
  const episodeTitleFontSize = isLive ? '17px' : '15px';
  const episodeTitleLineHeight = isLive ? '22px' : '20px';

  const titleBlock = (
    <>
      {showShowTitleLine ? (
        <p
          className="font-normal"
          style={{
            color: titleTextColor,
            fontFamily: 'var(--wt-hyperlink-font, inherit)',
            fontSize: showTitleFontSize,
            lineHeight: showTitleLineHeight,
            margin: 0,
            marginBottom: '4px',
            padding: 0,
          }}
        >
          {show?.title?.toUpperCase()}
        </p>
      ) : null}
      <p
        className="font-bold w-full text-left"
        style={{
          color: titleTextColor,
          fontFamily: 'var(--wt-hyperlink-font, inherit)',
          fontSize: episodeTitleFontSize,
          lineHeight: episodeTitleLineHeight,
          margin: 0,
          padding: 0,
        }}
      >
        {title}
      </p>
    </>
  );

  return (
    <div
      {...anchorAttributes}
      className={classNames(styles.container, 'flex flex-col', !isLive && 'pointer-events-none')}
    >
      <TooltipWrapper
        portalId="podcast-episode-embed-container"
        tooltipText={tooltipText || ''}
        shouldMount={!!tooltipText}
        isTriggerAsChild
      >
        <div
          className="flex flex-col gap-2"
          style={
            {
              background: resolvedBackground,
              width: '100%',
              padding: '16px',
              borderStyle: 'solid',
              borderColor: 'var(--wt-hyperlink-border-color, #e5e7eb)',
              borderWidth:
                'var(--wt-hyperlink-border-top-width, 1px) var(--wt-hyperlink-border-right-width, 1px) var(--wt-hyperlink-border-bottom-width, 1px) var(--wt-hyperlink-border-left-width, 1px)',
              borderRadius: '12px',
              '--podcast-embed-image-width-pct': '20%',
              // Keep image column width aligned with email rendering.
            } as CSSProperties
          }
        >
          <div
            className={classNames(
              'flex w-full gap-3',
              !isLive && showImage && 'flex-row items-center gap-5',
              isLive && showImage && 'flex-col items-center sm:flex-row sm:gap-5',
              !showImage && 'flex-col',
            )}
          >
            {showImage ? (
              <div
                className={classNames(
                  'flex shrink-0 justify-center',
                  !isLive && styles.artworkColumnWriteMode,
                  isLive && styles.artworkColumn,
                )}
                style={{ display: 'flex', alignItems: alignItems }}
              >
                <img src={displayArtworkUrl} alt={title} className="h-auto w-full max-w-full rounded-md object-cover" />
              </div>
            ) : null}
            <div
              className={classNames(
                'flex min-w-0 w-full flex-col',
                showImage && isLive && 'sm:flex-1',
                showImage && !isLive && 'flex-1',
              )}
            >
              {titleBlock}
              <div className={classNames('w-full', isEmail ? 'mt-2.5' : 'mt-1.5')}>
                <AudioPlayer
                  {...audioPlayerSharedProps}
                  progressBarHeight={isLive ? 4 : 3}
                  showEmailPlayReference={isEmail}
                  compactVerticalPadding={isLive}
                  emailPlayIconTone={textColorTone}
                  emailPlayButtonColor={toneColors.text}
                />
              </div>
            </div>
          </div>
        </div>
      </TooltipWrapper>
    </div>
  );
};
